import React, { useEffect, useRef, useState } from 'react';
import {
  formatOvertimeDateTime, formatOvertimeMinutes, fromJstDateTimeInput,
  loadOvertimeContext, OVERTIME_STATUS_LABELS, submitOvertimeReport,
  toJstDateTimeInput, validateOvertimePeriod,
  type OvertimeClient, type OvertimeContext
} from './overtimeApi';

interface Props {
  supabaseClient: OvertimeClient | null;
  employeeCode: string;
  employeeName: string;
  attendanceId: string;
  onEligibilityResolved?: (eligible: boolean | null) => void;
  onSavingChange?: (saving: boolean) => void;
}

const EmployeeOvertimePanel = ({
  supabaseClient, employeeCode, employeeName, attendanceId, onEligibilityResolved, onSavingChange
}: Props) => {
  const [context, setContext] = useState<OvertimeContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const savingRef = useRef(false);
  const callbackRef = useRef(onEligibilityResolved);
  callbackRef.current = onEligibilityResolved;
  const savingCallbackRef = useRef(onSavingChange);
  savingCallbackRef.current = onSavingChange;
  useEffect(() => () => { savingCallbackRef.current?.(false); }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setContext(null);
    setEditing(false);
    setError(null);
    setSaved(false);
    callbackRef.current?.(null);
    const load = async () => {
      try {
        if (!supabaseClient) throw new Error('通信状態を確認して、再読み込みしてください。');
        const result = await loadOvertimeContext(supabaseClient, employeeCode, attendanceId);
        if (!active) return;
        setContext(result);
        callbackRef.current?.(result.eligible || Boolean(result.report) ? true : result.reason === 'not_exceeded' ? false : null);
      } catch (err) {
        if (!active) return;
        setError(err instanceof Error ? err.message : '勤務予定を確認できませんでした。');
        callbackRef.current?.(null);
      } finally {
        if (active) setLoading(false);
      }
    };
    void load();
    return () => { active = false; };
  }, [supabaseClient, employeeCode, attendanceId, reload]);

  const openForm = () => {
    if (!context?.eligible) return;
    const firstMinute = context.scheduledEndAt && context.clockInAt
      ? new Date(Math.max(Date.parse(context.scheduledEndAt), Date.parse(context.clockInAt))).toISOString()
      : context.scheduledEndAt;
    setStart(toJstDateTimeInput(context.report?.startAt ?? firstMinute));
    setEnd(toJstDateTimeInput(context.report?.endAt ?? context.clockOutAt));
    setReason(context.report?.reason ?? '');
    setError(null);
    setSaved(false);
    setEditing(true);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (savingRef.current || !context || !supabaseClient) return;
    const validation = validateOvertimePeriod(context, start, end, reason);
    if (typeof validation === 'string') { setError(validation); return; }
    savingRef.current = true;
    setSaving(true);
    savingCallbackRef.current?.(true);
    setError(null);
    try {
      const report = await submitOvertimeReport(supabaseClient, {
        attendanceId, employeeCode, startAt: validation.startAt, endAt: validation.endAt,
        reason, expectedVersion: context.report?.version ?? null
      });
      setContext({ ...context, report });
      setEditing(false);
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : '時間外報告を保存できませんでした。退勤打刻は保存済みです。');
    } finally {
      savingRef.current = false;
      setSaving(false);
      savingCallbackRef.current?.(false);
    }
  };

  const startIso = fromJstDateTimeInput(start);
  const endIso = fromJstDateTimeInput(end);
  const minutes = startIso && endIso ? Math.floor((Date.parse(endIso) - Date.parse(startIso)) / 60000) : 0;
  const report = context?.report;

  return (
    <section aria-label="時間外勤務の確認" className="my-5 rounded-xl border border-blue-200 bg-blue-50 p-4 text-left">
      {loading && <p role="status" className="text-blue-800">勤務予定を確認しています…</p>}
      {error && <div role="alert" className="mb-3 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>}
      {!loading && !context && (
        <>
          <p className="mb-3 text-sm text-gray-700">退勤打刻は保存済みです。時間外勤務の対象か確認できていません。</p>
          <button type="button" className="rounded border border-blue-400 px-4 py-2 text-blue-800" onClick={() => setReload(value => value + 1)}>勤務予定を再確認</button>
        </>
      )}
      {context && !context.eligible && !report && (
        context.reason === 'not_exceeded'
          ? <p className="text-sm text-gray-700">予定終了時刻を超過していないため、時間外報告は不要です。</p>
          : <>
              <p className="text-sm text-amber-900">勤務予定が未設定、または判定に必要な情報が不足しています。管理者に確認してください。退勤打刻は保存済みです。</p>
              <button type="button" className="mt-3 text-sm text-blue-800 underline" onClick={() => setReload(value => value + 1)}>勤務予定を再確認</button>
            </>
      )}
      {saved && <p role="status" className="mb-3 font-medium text-green-800">時間外勤務の報告を保存しました。</p>}
      {report && !editing && (
        <div className="mb-3 space-y-1 text-sm text-gray-800">
          <p className="font-semibold">報告済み・{OVERTIME_STATUS_LABELS[report.status]}</p>
          <p>勤務日: {report.workDate} ／ {formatOvertimeMinutes(report.durationMinutes)}</p>
          <p>{formatOvertimeDateTime(report.startAt)} ～ {formatOvertimeDateTime(report.endAt)}</p>
          <p className="whitespace-pre-wrap break-words">理由: {report.reason}</p>
          <p>提出日時: {formatOvertimeDateTime(report.submittedAt)}</p>
          {report.adminNote && <p className="whitespace-pre-wrap break-words">管理者から: {report.adminNote}</p>}
          {!context?.eligible && <p className="text-amber-900">{report.status === 'cancelled' ? 'この報告は取り消されています。現在の勤務予定では時間外勤務の対象外です。' : '現在の勤務予定では対象外となっています。訂正が必要な場合は管理者に確認してください。'}</p>}
        </div>
      )}
      {context?.eligible && !editing && (
        <>
          {!report && <p className="mb-3 text-sm text-blue-900">予定終了 {formatOvertimeDateTime(context.scheduledEndAt)} を超過しています。</p>}
          <button type="button" onClick={openForm} className="w-full rounded-lg bg-blue-600 px-4 py-3 font-bold text-white hover:bg-blue-700">
            {report ? '時間外勤務の報告を訂正する' : '時間外勤務を報告する'}
          </button>
          <p className="mt-2 text-xs text-gray-600">後から従業員選択画面で勤務日を選び、報告・訂正できます。</p>
        </>
      )}
      {context && editing && (
        <form onSubmit={submit} className="space-y-4">
          <h3 className="text-lg font-bold text-gray-900">時間外勤務届</h3>
          <div className="text-sm text-gray-800"><p>従業員氏名: {employeeName}</p><p>勤務日: {context.workDate}</p></div>
          <p className="text-sm text-gray-600">実際に時間外勤務をした時間帯を入力してください。提出日時は保存時に記録します。</p>
          <label className="block text-sm font-medium">時間外勤務の開始日時
            <input aria-label="時間外勤務の開始日時" type="datetime-local" required value={start} disabled={saving}
              onChange={event => setStart(event.target.value)} className="mt-1 w-full rounded border border-gray-300 p-3" />
          </label>
          <label className="block text-sm font-medium">時間外勤務の終了日時
            <input aria-label="時間外勤務の終了日時" type="datetime-local" required value={end} disabled={saving}
              onChange={event => setEnd(event.target.value)} className="mt-1 w-full rounded border border-gray-300 p-3" />
          </label>
          <p className="text-sm text-gray-600">日をまたぐ場合は終了日も変更してください。</p>
          <p className="font-semibold text-blue-900" aria-live="polite">申告時間: {formatOvertimeMinutes(Math.max(0, minutes))}</p>
          <label className="block text-sm font-medium">時間外勤務をした理由
            <textarea aria-label="時間外勤務をした理由" value={reason} onChange={event => setReason(event.target.value)}
              required maxLength={2000} rows={3} disabled={saving} className="mt-1 w-full rounded border border-gray-300 p-3" />
          </label>
          {report?.status === 'reviewed' && <p className="text-sm text-amber-900">訂正後は未確認に戻り、管理者が再確認します。</p>}
          {report?.status === 'cancelled' && <p className="text-sm text-amber-900">再提出すると未確認に戻り、管理者が再確認します。</p>}
          <div className="flex flex-wrap gap-2">
            <button type="submit" disabled={saving} className="rounded bg-blue-600 px-4 py-3 font-semibold text-white disabled:opacity-50">{saving ? '報告を保存・確認中…' : '報告を保存する'}</button>
            <button type="button" disabled={saving} onClick={() => setEditing(false)} className="rounded border border-gray-300 bg-white px-4 py-3 disabled:opacity-50">入力を閉じる</button>
          </div>
          {error && <button type="button" disabled={saving} onClick={() => setReload(value => value + 1)} className="text-sm text-blue-800 underline">最新の報告を読み直す（入力内容は破棄されます）</button>}
        </form>
      )}
    </section>
  );
};

export default EmployeeOvertimePanel;
